import { buildSearchParams, request } from "utils/proxy";
import { API_FUNDING_PROGRAM } from "globals/constants";


/**
 * Short Funding Program Data Object
 *
 * @typedef {Object} ShortFundingProgram
 *
 * @property {string} fundingProgramName - The name of the funding program.
 * @property {string} fundingProgramId - The unique ID of the funding program.
 * @property {string} organizationName - The name of the organization associated with the funding program.
 */

/**
 * Funding Program Data Object
 *
 * @typedef {Object} FundingProgram
 *
 * @property {boolean} active - Indicates whether the funding program is active (true) or inactive (false).
 * @property {string} adminUpdateDate - The date and time when the funding program was last updated.
 * @property {string} description - The description of the funding program.
 * @property {string} name - The name of the funding program.
 * @property {string} fundingProgramId - The unique ID of the funding program.
 * @property {string} organizationId - The ID of the organization associated with the funding program.
 * @property {string} organizationName - The name of the organization associated with the funding program.
 * @property {string} tenantId - The ID of the tenant.
 */

/**
 *
 * @template {object} T
 *
 * @typedef {Object<T>} ListResponse
 *
 * @property {T[]} content - The list of short funding program objects.
 * @property {number} totalElements - The total number of elements.
 * @property {number} totalPages - The total number of pages.
 * @property {boolean} last - Indicates whether this is the last page.
 * @property {boolean} first - Indicates whether this is the first page.
 * @property {number} number - The current page number.
 * @property {number} size - The number of items per page.
 * @property {number} numberOfElements - The number of elements on this page.
 * @property {boolean} empty - Indicates whether the list is empty.
 * @property {object} pageable - The pageable object.
 * @property {object} sort - The sort object.
 * @property {boolean} sort.sorted - Indicates whether the list is sorted.
 * @property {boolean} sort.empty - Indicates whether the list is empty.
 * @property {boolean} sort.unsorted - Indicates whether the list is unsorted.
 */

/**
 * A collection of functions for interacting with funding programs.
 * @namespace
 */
export const fundingPrograms = {
	/**
	 * Retrieve a list of funding programs for a specific organization.
	 *
	 * @param {object} params - Query parameters for filtering and pagination.
	 * @param {number} [params.page] - The page number for pagination.
	 * @param {number} [params.size] - The number of items per page.
	 * @param {string[]} [params.search] - A search term for filtering.
	 * @param {boolean} [params.activeOnly] - Indicates whether to only return active funding programs.
	 *
	 * @returns {Promise<ListResponse<ShortFundingProgram>>} A Promise that resolves with the list of funding programs.
	 */
	findAll: (params) => {
		return request(
			`${API_FUNDING_PROGRAM}/search${buildSearchParams(params)}`
		);
	},

	/**
	 * Retrieve information about a specific funding program by its ID.
	 *
	 * @param {string} id - The ID of the funding program.
	 *
	 * @returns {Promise<FundingProgram>} A Promise that resolves with the funding program details.
	 */
	findById: (id) => {
		return request(`${API_FUNDING_PROGRAM}/${id}`);
	},
};
